import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import katex from "katex";
import "katex/dist/katex.min.css";
import { MODELS } from "../../constants/models";
import { exportToFile } from "../../services/ChatService";
import { toast } from "react-toastify";

// ── KaTeX helper ──────────────────────────────────────────────────────────────
function renderKatex(formula, displayMode) {
  try {
    return katex.renderToString(formula.trim(), {
      throwOnError: false,
      displayMode,
      trust: false,
    });
  } catch {
    return formula;
  }
}

// ── Copy helpers: inline dùng $...$, display dùng $$...$$ ────────────────────
function wrapLatexForCopy(formula, displayMode = false) {
  const latex = String(formula || "").trim();
  if (!latex) return "";

  return `$${latex}$`;
}

function isBlockLikeElement(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;

  const tagName = element.tagName.toLowerCase();
  return [
    "div",
    "p",
    "pre",
    "section",
    "article",
    "header",
    "footer",
    "ul",
    "ol",
    "li",
    "table",
    "thead",
    "tbody",
    "tr",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
  ].includes(tagName);
}

function cleanCopiedText(text) {
  return String(text || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function fragmentToPlainTextWithLatex(root) {
  const parts = [];

  function append(text) {
    if (!text) return;
    parts.push(text);
  }

  function appendNewline(force = false) {
    const current = parts.join("");
    if (!current) return;

    if (force) {
      if (!current.endsWith("\n")) parts.push("\n");
      return;
    }

    if (!current.endsWith("\n")) parts.push("\n");
  }

  function walk(node) {
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      append(node.textContent || "");
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node;
    const tagName = element.tagName.toLowerCase();

    if (tagName === "br") {
      appendNewline(true);
      return;
    }

    if (element.classList.contains("copyable-katex")) {
      const latex = element.getAttribute("data-latex") || "";
      const displayMode = element.getAttribute("data-display-mode") === "true";
      append(wrapLatexForCopy(latex, displayMode));
      return;
    }

    if (tagName === "img") {
      const alt = element.getAttribute("alt");
      if (alt) append(`[${alt}]`);
      return;
    }

    if (tagName === "pre") {
      appendNewline();
      append(element.innerText || element.textContent || "");
      appendNewline();
      return;
    }

    if (tagName === "li") {
      append("- ");
      Array.from(element.childNodes).forEach(walk);
      appendNewline();
      return;
    }

    const shouldAddNewlineBefore = isBlockLikeElement(element);
    if (shouldAddNewlineBefore) appendNewline();

    Array.from(element.childNodes).forEach(walk);

    const shouldAddNewlineAfter = isBlockLikeElement(element);
    if (shouldAddNewlineAfter) appendNewline();
  }

  Array.from(root.childNodes).forEach(walk);

  return cleanCopiedText(parts.join(""));
}

function fragmentToPlainHtmlWithLatex(root) {
  const clone = root.cloneNode(true);

  // Xóa toast nếu vô tình nằm trong vùng copy
  clone.querySelectorAll(".latex-copy-toast").forEach((node) => {
    node.remove();
  });

  // Đổi KaTeX rendered HTML thành LaTeX text trong HTML clipboard
  clone.querySelectorAll(".copyable-katex").forEach((node) => {
    const latex = node.getAttribute("data-latex") || "";
    const displayMode = node.getAttribute("data-display-mode") === "true";
    const text = wrapLatexForCopy(latex, displayMode);

    const replacement = document.createElement(displayMode ? "div" : "span");
    replacement.textContent = text;

    if (displayMode) {
      replacement.setAttribute(
        "style",
        [
          "display:block",
          "text-align:center",
          "margin:8px 0",
          "font-family:'Cambria Math','Times New Roman',serif",
        ].join(";"),
      );
    } else {
      replacement.setAttribute(
        "style",
        "font-family:'Cambria Math','Times New Roman',serif",
      );
    }

    node.replaceWith(replacement);
  });

  // Dọn attribute không cần thiết, nhưng giữ tag strong/em/ul/ol/li/p
  clone
    .querySelectorAll("[data-latex], [data-display-mode], [data-copied]")
    .forEach((node) => {
      node.removeAttribute("data-latex");
      node.removeAttribute("data-display-mode");
      node.removeAttribute("data-copied");
    });

  return clone.innerHTML.trim();
}

function wrapHtmlForClipboard(html) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
</head>
<body>
${html}
</body>
</html>`;
}

let latexSelectionCopyHandlerInstalled = false;

function installLatexSelectionCopyHandler() {
  if (latexSelectionCopyHandlerInstalled || typeof document === "undefined") {
    return;
  }

  latexSelectionCopyHandlerInstalled = true;

  document.addEventListener(
    "copy",
    (event) => {
      const selection = window.getSelection();

      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);

      const commonElement =
        range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;

      if (!commonElement) return;

      const isInsideChatMessage = commonElement.closest?.(
        "[data-chat-message]",
      );

      if (!isInsideChatMessage) return;

      // Clone riêng cho plain text
      const textFragment = range.cloneContents();
      const textContainer = document.createElement("div");
      textContainer.appendChild(textFragment);
      const plainText = fragmentToPlainTextWithLatex(textContainer);

      // Clone riêng cho rich HTML
      const htmlFragment = range.cloneContents();
      const htmlContainer = document.createElement("div");
      htmlContainer.appendChild(htmlFragment);
      const htmlText = fragmentToPlainHtmlWithLatex(htmlContainer);

      if (!plainText && !htmlText) return;

      event.preventDefault();

      // Fallback cho nơi chỉ nhận text thường
      event.clipboardData.setData("text/plain", plainText);

      // Quan trọng: Word, Excel, Google Docs sẽ ưu tiên text/html
      event.clipboardData.setData("text/html", wrapHtmlForClipboard(htmlText));
    },
    true,
  );
}

// ── Copyable KaTeX: click từng công thức để copy LaTeX ───────────────────────
function CopyableKatex({ formula, displayMode = false }) {
  const [copied, setCopied] = useState(false);
  const [toastStyle, setToastStyle] = useState(null);
  const hideTimerRef = useRef(null);
  const latex = String(formula || "").trim();

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // QUAN TRỌNG: lấy target + rect trước mọi await
    const target = e.currentTarget;
    const rect = target?.getBoundingClientRect?.();

    const textToCopy = wrapLatexForCopy(latex, displayMode);

    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = textToCopy;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    // Nếu không lấy được rect thì vẫn copy thành công, chỉ không hiện tooltip
    if (!rect) {
      setCopied(true);

      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
      }

      hideTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        setToastStyle(null);
      }, 900);

      return;
    }

    const shouldShowBelow = rect.top < 48;

    setToastStyle({
      left: rect.left + rect.width / 2,
      top: shouldShowBelow ? rect.bottom + 8 : rect.top - 8,
      transform: shouldShowBelow
        ? "translateX(-50%)"
        : "translate(-50%, -100%)",
    });

    setCopied(true);

    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
    }

    hideTimerRef.current = window.setTimeout(() => {
      setCopied(false);
      setToastStyle(null);
    }, 900);
  };

  const html = renderKatex(latex, displayMode);
  const Tag = displayMode ? "div" : "span";

  return (
    <>
      <Tag
        className={`copyable-katex ${displayMode ? "copyable-katex-display" : "copyable-katex-inline"
          }`}
        title="Click để copy LaTeX"
        onClick={handleCopy}
        data-latex={latex}
        data-display-mode={displayMode ? "true" : "false"}
        data-copied={copied ? "true" : "false"}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {copied &&
        toastStyle &&
        createPortal(
          <div
            className="latex-copy-toast"
            style={{
              position: "fixed",
              left: toastStyle.left,
              top: toastStyle.top,
              transform: toastStyle.transform,
              zIndex: 999999,
            }}
          >
            Đã copy LaTeX
          </div>,
          document.body,
        )}
    </>
  );
}

// ── Inline parser: inline math + bold / italic / code ─────────────────────────
// Returns an array of React nodes.
function parseInline(text, baseKey = 0) {
  if (!text) return [];
  const nodes = [];
  let k = baseKey;

  // Order matters: \(...\) and $...$ before **, *
  const RE =
    /(\\\([\s\S]*?\\\)|\$(?!\$)[^\n$]*?\$|\*\*[\s\S]*?\*\*|__[\s\S]*?__|(?<!\*)\*(?!\*)([^*\n]+?)(?<!\*)\*(?!\*)|`[^`\n]+?`)/g;

  let last = 0;
  let m;
  while ((m = RE.exec(text)) !== null) {
    // plain text before match
    if (m.index > last) {
      const chunk = text.slice(last, m.index);
      chunk.split("\n").forEach((line, i) => {
        if (i > 0) nodes.push(<br key={k++} />);
        if (line) nodes.push(line);
      });
    }

    const raw = m[0];

    if (raw.startsWith("\\(")) {
      nodes.push(
        <CopyableKatex
          key={k++}
          formula={raw.slice(2, -2)}
          displayMode={false}
        />,
      );
    } else if (raw.startsWith("$")) {
      nodes.push(
        <CopyableKatex
          key={k++}
          formula={raw.slice(1, -1)}
          displayMode={false}
        />,
      );
    } else if (raw.startsWith("**") || raw.startsWith("__")) {
      const inner = raw.slice(2, -2);

      nodes.push(<strong key={k++}>{parseInline(inner, k * 1000)}</strong>);
    } else if (raw.startsWith("*")) {
      const inner = raw.slice(1, -1);

      nodes.push(<em key={k++}>{parseInline(inner, k * 1000)}</em>);
    } else if (raw.startsWith("`")) {
      nodes.push(
        <code
          key={k++}
          style={{
            background: "#f3f4f6",
            borderRadius: 3,
            padding: "1px 5px",
            fontSize: "0.85em",
            fontFamily: "Consolas, monospace",
            color: "#cd1627",
          }}
        >
          {raw.slice(1, -1)}
        </code>,
      );
    }

    last = m.index + raw.length;
  }

  // trailing text
  if (last < text.length) {
    text
      .slice(last)
      .split("\n")
      .forEach((line, i) => {
        if (i > 0) nodes.push(<br key={k++} />);
        if (line) nodes.push(line);
      });
  }

  return nodes;
}

function isMarkdownTableRow(line) {
  const trimmed = String(line || "").trim();
  return trimmed.startsWith("|") && trimmed.indexOf("|", 1) !== -1;
}

function isMarkdownTableSeparator(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed.startsWith("|")) return false;

  const cells = trimmed.replace(/^\|/, "").replace(/\|$/, "").split("|");
  return (
    cells.length > 0 &&
    cells.every((cell) => /^[\s\-:]+$/.test(cell.trim()))
  );
}

function parseMarkdownTableRow(line) {
  const trimmed = String(line || "").trim();
  let inner = trimmed;
  if (inner.startsWith("|")) inner = inner.slice(1);
  if (inner.endsWith("|")) inner = inner.slice(0, -1);
  return inner.split("|").map((cell) => cell.trim());
}

function renderMarkdownTable(headerLine, bodyLines, key) {
  const headerCells = parseMarkdownTableRow(headerLine);
  const bodyRows = bodyLines.map(parseMarkdownTableRow);
  const colCount = Math.max(
    headerCells.length,
    ...bodyRows.map((row) => row.length),
    1,
  );

  const padRow = (cells) => {
    const next = [...cells];
    while (next.length < colCount) next.push("");
    return next.slice(0, colCount);
  };

  return (
    <div key={key} className="pim-table-responsive">
      <table className="pim-md-table">
        <thead>
          <tr>
            {padRow(headerCells).map((cell, idx) => (
              <th key={idx}>{parseInline(cell, idx * 100)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {padRow(row).map((cell, cellIdx) => (
                <td key={cellIdx}>
                  {parseInline(cell, rowIdx * 1000 + cellIdx * 10)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Block parser ──────────────────────────────────────────────────────────────
function parseContent(raw) {
  if (!raw) return null;

  const blocks = [];
  let k = 0;

  // ① Split fenced code blocks  ```lang\n...\n```
  const segs = [];
  const CODE_RE = /```(\w*)\n?([\s\S]*?)```/g;
  let lastIdx = 0;
  let m;
  while ((m = CODE_RE.exec(raw)) !== null) {
    if (m.index > lastIdx) {
      segs.push({ t: "text", v: raw.slice(lastIdx, m.index) });
    }
    segs.push({ t: "code", lang: m[1] || "", v: m[2].trimEnd() });
    lastIdx = m.index + m[0].length;
  }

  if (lastIdx < raw.length) {
    segs.push({ t: "text", v: raw.slice(lastIdx) });
  }

  // ② Within text segments, split display math  $$...$$ | \[...\]
  const DMATH_RE = /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\])/g;
  const parts = [];

  for (const seg of segs) {
    if (seg.t === "code") {
      parts.push(seg);
      continue;
    }

    DMATH_RE.lastIndex = 0;
    let li = 0;

    while ((m = DMATH_RE.exec(seg.v)) !== null) {
      if (m.index > li) {
        parts.push({ t: "text", v: seg.v.slice(li, m.index) });
      }

      const formula = m[0].startsWith("$$")
        ? m[0].slice(2, -2)
        : m[0].slice(2, -2); // both $$.$$ and \[.\] strip 2 chars each side

      parts.push({ t: "dmath", formula });
      li = m.index + m[0].length;
    }

    if (li < seg.v.length) {
      parts.push({ t: "text", v: seg.v.slice(li) });
    }
  }

  // ③ Render each part
  for (const part of parts) {
    // Code block
    if (part.t === "code") {
      blocks.push(
        <pre
          key={k++}
          style={{
            background: "#1e1e2e",
            color: "#cdd6f4",
            borderRadius: 8,
            padding: "12px 16px",
            overflowX: "auto",
            fontSize: 13,
            lineHeight: 1.65,
            margin: "8px 0",
            fontFamily: "Consolas, Monaco, 'Courier New', monospace",
          }}
        >
          {part.lang && (
            <div
              style={{
                color: "#6c7086",
                fontSize: 11,
                marginBottom: 6,
                fontFamily: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {part.lang}
            </div>
          )}
          <code style={{ fontFamily: "inherit" }}>{part.v}</code>
        </pre>,
      );
      continue;
    }

    // Display math
    if (part.t === "dmath") {
      blocks.push(
        <div
          key={k++}
          style={{
            overflowX: "auto",
            margin: "6px 0",
            padding: "2px 0",
          }}
        >
          <CopyableKatex formula={part.formula} displayMode={true} />
        </div>,
      );
      continue;
    }

    // Text: iterate line by line, detect headings / HR / lists
    const lines = part.v.split("\n");
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trimEnd();

      // blank line
      if (!line.trim()) {
        i++;
        continue;
      }

      // Heading  # / ## / ###
      const hm = line.match(/^(#{1,3})\s+(.+)$/);
      if (hm) {
        const lvl = hm[1].length;
        const sz = ["17px", "15px", "14px"][lvl - 1];

        blocks.push(
          <p
            key={k++}
            style={{
              fontSize: sz,
              fontWeight: 700,
              margin: "10px 0 3px",
              lineHeight: 1.4,
            }}
          >
            {parseInline(hm[2], k)}
          </p>,
        );

        i++;
        continue;
      }

      // Horizontal rule ---
      if (/^-{3,}$/.test(line.trim())) {
        blocks.push(
          <hr
            key={k++}
            style={{
              margin: "8px 0",
              borderColor: "#e5e7eb",
              borderTopWidth: 1,
            }}
          />,
        );

        i++;
        continue;
      }

      // Bullet list  - / * / +
      if (/^[\s]*[-*+]\s+/.test(line)) {
        const items = [];

        while (i < lines.length) {
          const l = lines[i].trimEnd();
          const bm = l.match(/^[\s]*[-*+]\s+(.+)$/);

          if (bm) {
            items.push(bm[1]);
            i++;
          } else if (!l.trim()) {
            i++;
            break;
          } else {
            break;
          }
        }

        blocks.push(
          <ul
            key={k++}
            style={{
              paddingLeft: 20,
              margin: "4px 0",
              listStyleType: "disc",
            }}
          >
            {items.map((item, idx) => (
              <li key={idx} style={{ margin: "2px 0", lineHeight: 1.65 }}>
                {parseInline(item, idx * 100)}
              </li>
            ))}
          </ul>,
        );

        continue;
      }

      // Numbered list  1. / 2. …
      if (/^[\s]*\d+\.\s+/.test(line)) {
        const items = [];

        while (i < lines.length) {
          const l = lines[i].trimEnd();
          const nm = l.match(/^[\s]*\d+\.\s+(.+)$/);

          if (nm) {
            items.push(nm[1]);
            i++;
          } else if (!l.trim()) {
            i++;
            break;
          } else {
            break;
          }
        }

        blocks.push(
          <ol
            key={k++}
            style={{
              paddingLeft: 20,
              margin: "4px 0",
              listStyleType: "decimal",
            }}
          >
            {items.map((item, idx) => (
              <li key={idx} style={{ margin: "2px 0", lineHeight: 1.65 }}>
                {parseInline(item, idx * 100)}
              </li>
            ))}
          </ol>,
        );

        continue;
      }

      // Markdown table  | h1 | h2 |
      if (
        isMarkdownTableRow(line) &&
        i + 1 < lines.length &&
        isMarkdownTableSeparator(lines[i + 1])
      ) {
        const headerLine = line;
        const bodyLines = [];
        i += 2;

        while (i < lines.length) {
          const rowLine = lines[i].trimEnd();
          if (!rowLine.trim()) {
            i++;
            break;
          }
          if (!isMarkdownTableRow(rowLine) || isMarkdownTableSeparator(rowLine)) {
            break;
          }
          bodyLines.push(rowLine);
          i++;
        }

        blocks.push(renderMarkdownTable(headerLine, bodyLines, k++));
        continue;
      }

      // Normal line
      blocks.push(
        <p key={k++} style={{ margin: "3px 0", lineHeight: 1.75 }}>
          {parseInline(line, k * 1000)}
        </p>,
      );

      i++;
    }
  }

  return blocks;
}

// ── Reasoning Trace block ─────────────────────────────────────────────────────
function ReasoningTrace({ content, streaming }) {
  const [expanded, setExpanded] = useState(true);
  const scrollRef = useRef(null);
  const hasContent = !!(content && String(content).trim());

  useEffect(() => {
    if (!expanded) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [content, expanded]);

  if (!hasContent && !streaming) return null;

  return (
    <div className="pim-reasoning">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="pim-reasoning-header"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "#8b5cf6", flexShrink: 0 }}
        >
          <path d="M12 2a4 4 0 0 1 4 4c0 .34-.04.67-.1 1H8.1A4.02 4.02 0 0 1 8 6a4 4 0 0 1 4-4z" />
          <path d="M9 7H5a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h1" />
          <path d="M15 7h4a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1" />
          <path d="M9 12v7a2 2 0 0 0 4 0v-7" />
          <path d="M6 12v2a3 3 0 0 0 3 3" />
          <path d="M18 12v2a3 3 0 0 1-3 3" />
        </svg>

        <span className="flex-1 text-left">
          Reasoning Trace
          {streaming && (
            <span
              style={{
                color: "#8b5cf6",
                marginLeft: 6,
                fontWeight: 400,
                fontStyle: "italic",
              }}
            >
              đang suy nghĩ…
            </span>
          )}
        </span>

        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          style={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div ref={scrollRef} className="pim-reasoning-body">
          {hasContent ? (
            <>
              {content}
              {streaming && (
                <span
                  style={{
                    display: "inline-block",
                    width: 2,
                    height: "1em",
                    background: "#8b5cf6",
                    marginLeft: 3,
                    borderRadius: 1,
                    verticalAlign: "text-bottom",
                    animation: "pimStreamCaret 0.92s step-end infinite",
                  }}
                />
              )}
            </>
          ) : (
            <span style={{ fontStyle: "italic", color: "#9ca3af" }}>
              Đang nhận suy nghĩ…
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Streaming cursor (chỉ khi đã có chữ — tránh chỉ có vạch | lẻ loi) ────────
function StreamingCursor() {
  return (
    <span
      style={{
        display: "inline-block",
        width: 2,
        height: "1em",
        background: "#9ca3af",
        marginLeft: 3,
        borderRadius: 1,
        verticalAlign: "text-bottom",
        animation: "pimStreamCaret 0.92s step-end infinite",
      }}
    />
  );
}

/** Trạng thái chờ chunk đầu tiên — ba chấm sóng nhẹ, icon giống bot */
function AssistantThinkingPlaceholder() {
  return (
    <div
      className="flex items-center gap-2 py-0.5 select-none"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex gap-1.5 items-center">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="chat-thinking-dot rounded-full bg-gradient-to-br from-emerald-500 to-teal-600"
            style={{ animationDelay: `${i * 0.14}s` }}
          />
        ))}
      </div>
    </div>
  );
}

// ── AssistantBubble ───────────────────────────────────────────────────────────
function AssistantBubble({ content, thinkingContent, streaming }) {
  const blocks = useMemo(() => parseContent(content), [content]);
  const hasText = !!(content && String(content).trim());
  const hasThinking = !!(thinkingContent && String(thinkingContent).trim());

  useEffect(() => {
    installLatexSelectionCopyHandler();
  }, []);

  return (
    <div
      style={{
        fontSize: 14,
        lineHeight: 1.75,
        color: "#1f2937",
        wordBreak: "break-word",
      }}
    >
      <style>{`
        .chat-thinking-dot {
          width: 7px;
          height: 7px;
          opacity: .85;
          animation: pimChatThinkingWave 1.12s ease-in-out infinite;
        }

        @keyframes pimChatThinkingWave {
          0%, 85%, 100% {
            opacity: .28;
            transform: translateY(0);
          }
          45% {
            opacity: 1;
            transform: translateY(-4px);
          }
        }

        @keyframes pimStreamCaret {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0;
          }
        }

        .copyable-katex {
          position: relative;
          cursor: pointer;
          border-radius: 4px;
          transition:
            background-color .15s ease,
            box-shadow .15s ease;
        }

        .copyable-katex:hover {
          background: rgba(205, 22, 39, 0.08);
          box-shadow: 0 0 0 2px rgba(205, 22, 39, 0.08);
        }

        .latex-copy-toast {
          background: #111827;
          color: #ffffff;
          font-size: 11px;
          line-height: 1;
          white-space: nowrap;
          padding: 6px 8px;
          border-radius: 6px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.25);
          pointer-events: none;
        }

        .copyable-katex-display {
          display: block;
        }

        .copyable-katex-inline {
          display: inline-block;
        }
      `}</style>

      {/* {(hasThinking || (streaming && !hasText)) && (
        <ReasoningTrace content={thinkingContent || ""} streaming={streaming} />
      )} */}

      {hasText ? (
        <>
          {blocks}
          {streaming && <StreamingCursor />}
        </>
      ) : streaming ? (
        <AssistantThinkingPlaceholder />
      ) : hasThinking ? (
        <span style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>
          Chưa có phần trả lời cuối cùng từ AI.
        </span>
      ) : null}
    </div>
  );
}

function assistantModelLabel(modelKeyOrValue) {
  if (!modelKeyOrValue) return null;
  return (
    MODELS.find((m) => m.id === modelKeyOrValue)?.label ||
    MODELS.find((m) => m.value === modelKeyOrValue)?.label ||
    modelKeyOrValue
  );
}

/** Overlay phóng to ảnh — click nền / X / Escape để đóng */
function ImageLightbox({ src, onClose }) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };

    window.addEventListener("keydown", onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [handleClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Xem ảnh phóng to"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-4"
      onClick={handleClose}
      style={{ animation: "fadeIn 0.15s ease-out" }}
    >
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>

      <button
        type="button"
        className="absolute top-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white backdrop-blur-sm hover:bg-white/20 transition-colors"
        aria-label="Đóng"
        onClick={handleClose}
      >
        ×
      </button>

      <img
        src={src}
        alt="Xem ảnh lớn"
        className="max-h-[90vh] max-w-full object-contain rounded-lg shadow-2xl cursor-default select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>,
    document.body,
  );
}

/** Icon file theo extension */
function FileTypeIcon({ ext }) {
  const isPdf = ext === "pdf";
  const isWord = ext === "doc" || ext === "docx";
  const isSheet = ext === "xls" || ext === "xlsx" || ext === "csv";

  if (isPdf) {
    return (
      <svg
        className="w-4 h-4 flex-shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 13h1.5a1 1 0 010 2H9v-4h1.5a1 1 0 010 2M14 11v4m0 0h1m-1-2h1"
        />
      </svg>
    );
  }

  if (isWord) {
    return (
      <svg
        className="w-4 h-4 flex-shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12l1.5 4 1.5-3 1.5 3 1.5-4"
        />
      </svg>
    );
  }

  if (isSheet) {
    return (
      <svg
        className="w-4 h-4 flex-shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6M9 15h6M9 9h1"
        />
      </svg>
    );
  }

  return (
    <svg
      className="w-4 h-4 flex-shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

const EXT_STYLE = {
  pdf: "text-red-600 bg-red-50",
  doc: "text-blue-600 bg-blue-50",
  docx: "text-blue-600 bg-blue-50",
  xls: "text-green-600 bg-green-50",
  xlsx: "text-green-600 bg-green-50",
  csv: "text-green-600 bg-green-50",
  txt: "text-gray-600 bg-gray-100",
  md: "text-purple-600 bg-purple-50",
  json: "text-orange-600 bg-orange-50",
};

const EXT_LABEL = {
  js: "JavaScript",
  ts: "TypeScript",
  jsx: "React JSX",
  tsx: "React TSX",
  pdf: "PDF",
  doc: "Word",
  docx: "Word",
  xls: "Excel",
  xlsx: "Excel",
  csv: "CSV",
  txt: "Text",
  md: "Markdown",
  json: "JSON",
};

function DocFileChip({ name, url }) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  const style = EXT_STYLE[ext] || "text-gray-600 bg-gray-100";
  const typeLabel = EXT_LABEL[ext] || (ext ? ext.toUpperCase() : "File");

  const inner = (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className={`h-10 w-10 rounded-lg flex items-center justify-center ${style}`}
      >
        <FileTypeIcon ext={ext} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900 truncate">
          {name}
        </div>
        <div className="text-xs text-gray-500 truncate">{typeLabel}</div>
      </div>
    </div>
  );

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full max-w-[360px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-colors block"
        title={`Mở/tải ${name}`}
      >
        {inner}
      </a>
    );
  }

  return (
    <div
      className="w-full max-w-[360px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm"
      title={name}
    >
      {inner}
    </div>
  );
}

function stripAttachmentMarkers(content) {
  return (content || "")
    .replace(/\[Hình ảnh: [^\]]+\]\s*/g, "")
    .replace(/\[Tệp: [^\]]+\]\s*/g, "")
    .trim();
}

/**
 * User bubble:
 *  - `images`       (base64Url[])  — transient image previews
 *  - `attachedDocs` ({name,url}[]) — file attachment chips
 *  - `content`      (string)       — user's typed text only
 *  - `renderMath`   (boolean)      — khi true: render Markdown + KaTeX (dùng cho trang share)
 */
function UserBubble({ content, images, attachedDocs, renderMath = false }) {
  const displayContent = stripAttachmentMarkers(content);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const renderedBlocks = useMemo(
    () => (renderMath && displayContent ? parseContent(displayContent) : null),
    [renderMath, displayContent],
  );

  return (
    <div className="space-y-2">
      {lightboxUrl && (
        <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}

      {/* Inline image previews */}
      {images && images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((url, i) => (
            <button
              key={i}
              type="button"
              className="rounded-xl border border-gray-200 bg-white p-0 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 hover:opacity-95 transition-opacity cursor-zoom-in"
              title="Xem ảnh lớn"
              onClick={() => setLightboxUrl(url)}
            >
              <img
                src={url}
                alt={`Ảnh ${i + 1}`}
                className="block max-h-48 max-w-xs object-contain"
              />
            </button>
          ))}
        </div>
      )}

      {/* Document file chips */}
      {attachedDocs && attachedDocs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {attachedDocs.map((doc, i) => (
            <DocFileChip key={i} name={doc.name} url={doc.url} />
          ))}
        </div>
      )}

      {/* Typed text */}
      {displayContent && (
        renderMath ? (
          <div className="text-sm leading-relaxed break-words">
            {renderedBlocks}
          </div>
        ) : (
          <span className="whitespace-pre-wrap break-words">{displayContent}</span>
        )
      )}
    </div>
  );
}

// ── Export Dropdown ────────────────────────────────────────────────────────────
const EXPORT_FORMATS = [
  {
    group: "Tài liệu", items: [
      { ext: "docx", label: "Word (.docx)", icon: "📄" },
      { ext: "xlsx", label: "Excel (.xlsx)", icon: "📊" },
      { ext: "pptx", label: "PowerPoint (.pptx)", icon: "📽️" },
      { ext: "txt", label: "Text (.txt)", icon: "📝" },
      { ext: "md", label: "Markdown (.md)", icon: "📋" },
    ]
  },
  {
    group: "Code", items: [
      { ext: "js", label: "JavaScript (.js)", icon: "🟨" },
      { ext: "ts", label: "TypeScript (.ts)", icon: "🔷" },
      { ext: "py", label: "Python (.py)", icon: "🐍" },
      { ext: "go", label: "Go (.go)", icon: "🔵" },
      { ext: "java", label: "Java (.java)", icon: "☕" },
      { ext: "cpp", label: "C++ (.cpp)", icon: "⚙️" },
      { ext: "c", label: "C (.c)", icon: "🔧" },
      { ext: "cs", label: "C# (.cs)", icon: "💜" },
      { ext: "html", label: "HTML (.html)", icon: "🌐" },
      { ext: "css", label: "CSS (.css)", icon: "🎨" },
      { ext: "json", label: "JSON (.json)", icon: "📦" },
      { ext: "sql", label: "SQL (.sql)", icon: "🗃️" },
      { ext: "sh", label: "Shell (.sh)", icon: "🖥️" },
      { ext: "php", label: "PHP (.php)", icon: "🐘" },
      { ext: "rb", label: "Ruby (.rb)", icon: "💎" },
      { ext: "rs", label: "Rust (.rs)", icon: "🦀" },
      { ext: "swift", label: "Swift (.swift)", icon: "🍎" },
      { ext: "kt", label: "Kotlin (.kt)", icon: "🟣" },
      { ext: "vue", label: "Vue (.vue)", icon: "💚" },
      { ext: "yaml", label: "YAML (.yaml)", icon: "📐" },
    ]
  },
];

function ExportDropdown({ content }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleExport = async (ext) => {
    setExporting(true);
    try {
      const filename = "export_" + new Date().toISOString().slice(0, 10);
      await exportToFile(content, ext, filename);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Lỗi export: " + err.message);
    } finally {
      setExporting(false);
      setOpen(false);
    }
  };

  if (!content || !content.trim()) return null;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={exporting}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
        title="Export ra file"
      >
        {exporting ? (
          <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4" strokeLinecap="round" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
        Export
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-1 w-56 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          style={{ animation: "fadeIn 0.12s ease-out" }}
        >
          {EXPORT_FORMATS.map((group) => (
            <div key={group.group}>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 sticky top-0">
                {group.group}
              </div>
              {group.items.map((item) => (
                <button
                  key={item.ext}
                  type="button"
                  onClick={() => handleExport(item.ext)}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                >
                  <span className="text-sm">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Action bars ────────────────────────────────────────────────────────────────

function IconBtn({ title, onClick, disabled, children, variant }) {
  const base =
    "inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
  const active =
    variant === "active-up"
      ? "!text-emerald-600 bg-emerald-50"
      : variant === "active-down"
        ? "!text-red-600 bg-red-50"
        : "";
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${active}`}
    >
      {children}
    </button>
  );
}

function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }
  } catch { }
  // Fallback
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Smart copy giống Ctrl+C trong chat:
 *   - KaTeX formulas → $...$
 *   - Giữ bold / italic / list / heading qua HTML clipboard (paste vào Word, Docs)
 *   - Plain text clipboard dùng cho Notepad / terminal
 *
 * @param {HTMLElement|null} contentElement - DOM element chứa rendered content
 * @param {string} fallbackText             - Text thô nếu không truy cập được DOM
 */
async function copyMessageSmart(contentElement, fallbackText) {
  if (!contentElement) {
    return copyToClipboard(fallbackText || "");
  }

  // Clone riêng cho mỗi biến thể để replaceWith trong HTML path không phá plain path
  const textContainer = contentElement.cloneNode(true);
  const htmlContainer = contentElement.cloneNode(true);

  const plainText = fragmentToPlainTextWithLatex(textContainer);
  const htmlText = fragmentToPlainHtmlWithLatex(htmlContainer);
  const wrappedHtml = wrapHtmlForClipboard(htmlText);

  // Ưu tiên Clipboard API với ClipboardItem để set đồng thời text/plain + text/html
  try {
    if (
      typeof window !== "undefined" &&
      window.ClipboardItem &&
      navigator.clipboard?.write
    ) {
      const item = new window.ClipboardItem({
        "text/plain": new Blob([plainText], { type: "text/plain" }),
        "text/html": new Blob([wrappedHtml], { type: "text/html" }),
      });
      await navigator.clipboard.write([item]);
      return;
    }
  } catch (err) {
    console.warn("clipboard.write failed, falling back:", err);
  }

  // Fallback: chọn vùng HTML ẩn rồi execCommand("copy") — vẫn giữ format HTML
  try {
    const selection = window.getSelection();
    const prevRange =
      selection && selection.rangeCount > 0
        ? selection.getRangeAt(0).cloneRange()
        : null;

    const helper = document.createElement("div");
    helper.style.position = "fixed";
    helper.style.left = "-99999px";
    helper.style.top = "0";
    helper.setAttribute("contenteditable", "true");
    helper.innerHTML = wrappedHtml;
    document.body.appendChild(helper);

    const range = document.createRange();
    range.selectNodeContents(helper);
    selection.removeAllRanges();
    selection.addRange(range);

    document.execCommand("copy");

    document.body.removeChild(helper);
    selection.removeAllRanges();
    if (prevRange) selection.addRange(prevRange);
  } catch {
    // Cuối cùng: chỉ plain text (vẫn có $...$ cho công thức)
    copyToClipboard(plainText || fallbackText || "");
  }
}

// ── Edit user message inline ──────────────────────────────────────────────────

function UserEditMode({ initialContent, onSave, onCancel, disabled }) {
  const [value, setValue] = useState(initialContent || "");
  const taRef = useRef(null);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, []);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 300)}px`;
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel?.();
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSave?.(value);
    }
  };

  return (
    <div
      className="rounded-2xl bg-gray-100 px-4 py-3 w-[min(560px,90vw)] max-w-full"
    >
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className="w-full min-h-[60px] bg-transparent border-0 outline-none resize-none text-sm text-gray-900 leading-relaxed"
        rows={2}
      />
      <div className="mt-2 flex items-center justify-end gap-2 flex-wrap">
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 rounded-md disabled:opacity-50 whitespace-nowrap"
        >
          Hủy
        </button>
        <button
          type="button"
          onClick={() => onSave?.(value)}
          disabled={disabled || !value.trim()}
          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 whitespace-nowrap"
        >
          Lưu &amp; gửi lại
        </button>
      </div>
    </div>
  );
}

// ── MessageItem ───────────────────────────────────────────────────────────────
function MessageItem({
  message,
  formatTime,
  // New actions
  editingMessageId,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRegenerate,
  onBranch,
  onFeedback,
  isLoading,
  renderUserMath = false,
  // PIMATH actions
  onSaveMistake,
  onSimilarProblems,
  onFormulaCard,
  learningMode,
}) {
  const isUser = message.role === "user";
  const modelLabel = assistantModelLabel(message.model);
  const isEditing = editingMessageId && editingMessageId === message.id;
  const contentRef = useRef(null);

  const handleSmartCopy = () => {
    copyMessageSmart(contentRef.current, message.content || "");
  };

  // User row — right aligned with bubble
  if (isUser) {
    return (
      <div
        className="flex justify-end items-start gap-3 group"
        data-chat-message="true"
      >
        <div
          className="flex flex-col w-full min-w-0 items-end"
        >
          {isEditing ? (
            <UserEditMode
              initialContent={stripAttachmentMarkers(message.content)}
              disabled={isLoading}
              onSave={(v) => onSaveEdit?.(message.id, v)}
              onCancel={() => onCancelEdit?.()}
            />
          ) : (
            <div className="pim-bubble-user" ref={contentRef}>
              <UserBubble
                content={message.content}
                images={message.images}
                attachedDocs={message.attachedDocs}
                renderMath={renderUserMath}
              />
            </div>
          )}

          {!isEditing && (
            <div className="mt-1 flex items-center gap-1.5 pim-timestamp">
              <span>{formatTime(message.timestamp)}</span>
              {message.edited && (
                <span className="italic text-gray-400">· đã sửa</span>
              )}

              {/* Action bar (hover to show on desktop, always visible on touch) */}
              <div className="ml-1 flex items-center">
                <IconBtn
                  title="Sao chép"
                  onClick={handleSmartCopy}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </IconBtn>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Assistant row — left aligned, no bubble (ChatGPT-style)
  const handleFeedback = (kind) => {
    if (!onFeedback || !message.dbId) return;
    const next = message.feedback === kind ? null : kind;
    onFeedback(message.id, message.dbId, next);
  };

  return (
    <div className="flex items-start gap-3 group" data-chat-message="true">
      <div className="pim-assistant-avatar mt-0.5">
        <svg
          className="w-4 h-4 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <div className="mb-1 flex items-baseline gap-2 flex-wrap">
          <span className="pim-assistant-name">ChatBot PIMATH</span>
          {modelLabel && (
            <span className="pim-assistant-model">· {modelLabel}</span>
          )}
        </div>

        <div className="pim-assistant-content" ref={contentRef}>
          <AssistantBubble
            content={message.content}
            thinkingContent={message.thinkingContent || ""}
            streaming={!!message.streaming}
          />
        </div>

        <div className="mt-2 flex items-center gap-1.5 pim-timestamp flex-wrap">
          {message.streaming ? (
            <span className="text-[11px] text-emerald-700/70 font-medium">
              Đang trả lời…
            </span>
          ) : (
            <>
              <span>{formatTime(message.timestamp)}</span>

              {/* Action bar — hover để hiện */}
              <div className="flex items-center">
                <IconBtn
                  title="Sao chép"
                  onClick={handleSmartCopy}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </IconBtn>
                {onFeedback && message.dbId && (
                  <>
                    <IconBtn
                      title="Hữu ích"
                      onClick={() => handleFeedback("up")}
                      variant={message.feedback === "up" ? "active-up" : undefined}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                      </svg>
                    </IconBtn>
                    <IconBtn
                      title="Không hữu ích"
                      onClick={() => handleFeedback("down")}
                      variant={message.feedback === "down" ? "active-down" : undefined}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06L17 4m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                      </svg>
                    </IconBtn>
                  </>
                )}
              </div>

              {message.content && message.content.trim() && (
                <ExportDropdown content={message.content} />
              )}

              {/* PIMATH Quick Actions */}
              {message.content && message.content.trim() && !message.streaming && onSimilarProblems && (
                <button
                  type="button"
                  title="Tạo bài tương tự"
                  onClick={() => onSimilarProblems(message.content)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  📋 Bài tương tự
                </button>
              )}
              {message.content && message.content.trim() && !message.streaming && onFormulaCard && (
                <button
                  type="button"
                  title="Tóm tắt công thức"
                  onClick={() => onFormulaCard(message.content)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  📐 Công thức
                </button>
              )}
              {message.content && message.content.trim() && !message.streaming && onSaveMistake && learningMode === "mistake_check" && (
                <button
                  type="button"
                  title="Lưu vào sổ tay lỗi sai"
                  onClick={() => onSaveMistake(message.content)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                >
                  📓 Lưu lỗi sai
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageItem;
